import React, { useState, useEffect } from "react";
import styled from "styled-components";
import * as yup from "yup";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import { checkObjectValues } from "utils/checkEmptyValues";
import Spinner from "components/Spinner";
import trackAnalytics from "trackAnalytics";
import { ButtonRow, ConfirmModalContent, SeparateLine } from "styles/elements/main";
import { FormStyle } from "styles/forms/main";
import { Button, GenericError, ModalComponent } from "components";
import Api from "lib/api";
// eslint-disable-next-line import/no-cycle
import { PersonalInfo, WorkingInfo, CompanyInfo, PaymentInfo } from ".";
import { validationSchema } from "../validationSchema";
import { mapSupplierDataToApi } from "../config/mapUiToData";
import { useSelector } from "react-redux";
import { getUserRoles } from "modules/auth/slice";
import { SUPPLIER_PARTNER_TYPE } from "../staticData"
import { useParams, useHistory } from "react-router-dom/cjs/react-router-dom";

export const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 18px;
    color: #006bb4;
`;

function CreateSupplier({ onClose, setIsSupplierDataChange, onSuccess }) {

    const getCurrentUserRoles = useSelector(getUserRoles());
    const { id } = useParams()
    const { location } = useHistory()
    const isUserSupplierPartner = getCurrentUserRoles?.includes("supplier_partner_account_manager")

    const selectedOrganizationId = isUserSupplierPartner && location?.pathname?.includes("/database/clients") && id ? id : null

    const [newSupplierData, setNewSupplierData] = useState({
        arabicName: "",
        phoneNumber: "",
        email: "",
        nationalId: null,
        nationalIdFace: null,
        nationalIdBack: null,
        supplierPictureAttachments: null,
        supplierType: isUserSupplierPartner ? SUPPLIER_PARTNER_TYPE : null,
        truckTypes: null,
        workingGovernorates: null,
        hasACompany: null,
        companyName: "",
        commercialRegistrationAttachments: null,
        taxCardAttachments: null,
        paymentMethod: null,
        bankName: null,
        bankAccountNumber: "",
        ibanNumber: "",
        eWalletName: null,
        eWalletNumber: "",
        mezzaCardName: null,
        meezaCardNumber: "",
        paymentMethodDelete: false,
        accountHolderName: "",
        organizationId: { value: selectedOrganizationId }
    });
    const [createdSupplierData, setCreatedSupplierData] = useState([]);
    const [errorList, setErrorList] = useState([]);
    const [errors, setErrors] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [phoneNumberError, setPhoneNumberError] = useState(null);
    const [nationalIdError, setNationalIdError] = useState(null);
    const [eWalletNumberError, setEWalletNumberError] = useState(null);
    const [showCompanyAlert, setShowCompanyAlert] = useState(false);





    const handleSupplierDetailsChange = (e, name) => {
        if (!name) {

            const { name, value } = e?.target;

            setNewSupplierData({
                ...newSupplierData,
                [name]: value,
            });
            yup.reach(validationSchema, name)
                .validate(value)
                .then(() => setErrorList({ ...errorList, [name]: "" }))
                .catch((err) => setErrorList({ ...errorList, [name]: err.message }));
        } else {
            if (
                name === "hasACompany" &&
                e?.value === false &&
                newSupplierData.companyName !== ""
            ) {
                setShowCompanyAlert(true);
            } else {

                setNewSupplierData({
                    ...newSupplierData,
                    [name]: e,
                });
            }

            yup.reach(validationSchema, name)
                .validate(e)
                .then(() => setErrorList({ ...errorList, [name]: "" }))
                .catch((err) => setErrorList({ ...errorList, [name]: err.message }));
        }
    };

    const clearSupplierData = () => {
        setNewSupplierData({
            arabicName: "",
            phoneNumber: "",
            email: null,
            nationalId: null,
            nationalIdFace: null,
            nationalIdBack: null,
            supplierPictureAttachments: null,
            supplierType: "",
            truckTypes: null,
            workingGovernorates: null,
            hasACompany: null,
            companyName: "",
            commercialRegistrationAttachments: null,
            taxCardAttachments: null,
            paymentMethod: null,
            bankName: null,
            bankAccountNumber: "",
            ibanNumber: "",
            eWalletName: null,
            eWalletNumber: "",
            meezaCardName: null,
            meezaCardNumber: "",
            accountHolderName: "",
            organizationId: null
        });
    };

    const handleSubmitNewSupplier = async (e) => {
        e.preventDefault();
        trackAnalytics(`Supplier_profile_submit_creation`);

        try {
            const response = await validationSchema
                .validate(newSupplierData, { abortEarly: false })
                .then(async () => {
                    setIsLoading(true);
                    Api.post(`suppliers`, await mapSupplierDataToApi(newSupplierData), {
                        params: {
                            include: "paymentMethod",
                        },
                    })
                        .then(() => {
                            setIsLoading(false);
                            onSuccess(newSupplierData.arabicName);
                            clearSupplierData();
                        })
                        .catch((error) => {
                            setIsLoading(false);
                            setErrors(error.errors);
                            if (error?.errors?.phone_number) {
                                setPhoneNumberError(error?.errors?.phone_number?.[0]);
                            }
                            if (error?.errors?.national_id) {
                                setNationalIdError(error?.errors?.national_id?.[0]);
                            }
                            if (error?.errors?.["payment_method.e_wallet_number"]) {
                                setNationalIdError(
                                    error?.errors?.["payment_method.e_wallet_number"]?.[0]
                                );
                            }
                        });
                });

            setCreatedSupplierData(response.entites.suppliers);
            clearSupplierData();
        } catch (error) {
            const errorMessage = error?.inner?.reduce(
                (acc, error) => ({
                    ...acc,
                    [error?.path]: error?.message,
                }),
                {}
            );
            setErrorList(errorMessage);
        }
    };
    useEffect(() => {
        setPhoneNumberError(null);
    }, [newSupplierData.phoneNumber]);

    useEffect(() => {
        setNationalIdError(null);
    }, [newSupplierData.nationalId]);

    useEffect(() => {
        setEWalletNumberError(null);
    }, [newSupplierData.eWalletNumber]);

    useEffect(() => {
        if (checkObjectValues(newSupplierData)) {
            setIsSupplierDataChange(true);
        } else {
            setIsSupplierDataChange(false);
        }
    }, [newSupplierData]);

    return (
        <FormStyle>
            <SectionTitle>Personal Info</SectionTitle>
            <PersonalInfo
                newSupplierData={newSupplierData}
                setNewSupplierData={setNewSupplierData}
                handleSupplierDetailsChange={handleSupplierDetailsChange}
                isEditable
                errorList={errorList}
                phoneNumberError={phoneNumberError}
                nationalIdError={nationalIdError}
            />

            <SeparateLine />
            <SectionTitle>Working Info</SectionTitle>
            <WorkingInfo
                newSupplierData={newSupplierData}
                setNewSupplierData={setNewSupplierData}
                handleSupplierDetailsChange={handleSupplierDetailsChange}
                isEditable
                errorList={errorList}
            />
            <SeparateLine />
            <SectionTitle>Company Info</SectionTitle>

            <CompanyInfo
                newSupplierData={newSupplierData}
                setNewSupplierData={setNewSupplierData}
                handleSupplierDetailsChange={handleSupplierDetailsChange}
                isEditable
                errorList={errorList}
                errors={errors}
            />
            <SeparateLine />
            {showCompanyAlert && (
                <ModalComponent
                    header="Alert"
                    open={showCompanyAlert}
                    onClose={() => {
                        setShowCompanyAlert(false);
                    }}
                    onSubmit={() => {
                        setNewSupplierData({
                            ...newSupplierData,
                            hasACompany: { value: false, label: "No" },
                            companyName: "",
                            commercialRegistrationAttachments: null,
                            taxCardAttachments: null,
                        });
                        setShowCompanyAlert(!showCompanyAlert);
                    }}
                    submitBtnText="Change"
                    submitBtn
                    cancelBtn
                    isFooter
                >
                    <ConfirmModalContent>
                        by changing your choice from YES to NO, you will lose any uploaded data.
                    </ConfirmModalContent>
                </ModalComponent>
            )}
            <SectionTitle>Payments Info</SectionTitle>
            <PaymentInfo
                newSupplierData={newSupplierData}
                setNewSupplierData={setNewSupplierData}
                handleSupplierDetailsChange={handleSupplierDetailsChange}
                isEditable
                errorList={errorList}
                eWalletNumberError={eWalletNumberError}
            />

            {errors && <GenericError errorMassege={errors} />}

            <ButtonRow>
                <Button
                    type="button"
                    onChange={onClose}
                    name="Cancel"
                    btnStyle="secondBtn"
                />
                <Button
                    type="submit"
                    onChange={handleSubmitNewSupplier}
                    name={isLoading ? <Spinner /> : "Add"}
                    btnStyle="primaryBtn"
                    icon={<AddCircleOutlineOutlinedIcon />}
                />
            </ButtonRow>
        </FormStyle>
    );
}

export default CreateSupplier;
